import { Lang } from 'vuejs-localization';
import { IGameResponseViewModel, IGameClubViewModel, ChatRoomConfiguration, IGameChatRoomViewModel, IArcadeGameViewModel, IGameConfigurationService, IChatConfigurationService, IConfigurationService } from '@/app-modules/config';
import { WindowType, IWindowLocationPreference, UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { generateGuid } from '@/helper';
import { IChatModalLaunchOptions, IChatContextInteractionModalLaunchOptions, IModalLaunchOptions } from '@/plugins/modal-manager/models';
import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IUserChatStatusService } from '@/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service-interface';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { container } from '@/app-modules/_ioc';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { ChatContextInteractionType, ChatContextInteractionSource } from '@/components/chat/_domain/modules/chat-context-menu';
import { ITogglesService } from '@/app-modules/config';

const MODAL_SPAWN_EVENT_NAME = 'onSpawnModal';

interface IModalSpawnConfiguration {
  readonly gameConfiguration: IGameResponseViewModel[];
  readonly gameConfigurationForWinners: IGameResponseViewModel[];
  readonly chatRoomConfiguration: ChatRoomConfiguration[];
  readonly arcadeGameConfiguration: IArcadeGameViewModel[];
  readonly cmChatRoomId: number;
  readonly csChatRoomId: number;
}

interface IModalSpawnAgent {
  initialise(): Promise<IModalSpawnConfiguration>;
  getConfiguration(): IModalSpawnConfiguration | undefined;
  spawnCmChatModal(location?: IWindowLocationPreference): void;
  spawnCsChatModal(location?: IWindowLocationPreference): void;
  spawnChatModal(gameName: string, chatRoomId: number, chatRoomName?: string, location?: IWindowLocationPreference): void;
  trySpawnChatModalFromRoomName(roomName: string): IGameChatRoomViewModel;
  spawnGameModal(gameName: string, clubId: number, location?: IWindowLocationPreference): void;
  spawnArcadeGameModal(gameName: string, location?: IWindowLocationPreference): void;
  spawnWinnersModal(gameName: string, location?: IWindowLocationPreference): void;
  spawnSettingsModal(location?: IWindowLocationPreference): void;
  spawnPlayerNotesModal(location?: IWindowLocationPreference): void;
  spawnChatContextInteractionModal(chatContextInteractionType: ChatContextInteractionType, playerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, originalChatRoomName?: string, chatContextInteractionSource?: ChatContextInteractionSource, location?: IWindowLocationPreference): void;
}

@injectable()
class ModalSpawnAgent implements IModalSpawnAgent {
  private readonly settingsGuid = generateGuid();
  private readonly playerNotesGuid = generateGuid();
  private readonly $lang: Lang;
  private gameConfiguration: IGameResponseViewModel[] = [];
  private gameConfigurationForWinners: IGameResponseViewModel[] = [];
  private chatRoomConfiguration: ChatRoomConfiguration[] = [];
  private arcadeGameConfiguration: IArcadeGameViewModel[] = [];
  private cmChatRoomId: number;
  private csChatRoomId: number;
  private isInitialised: boolean = false;

  constructor(
    @inject(SERVICE_IDENTIFIERS.IGameConfigurationService)
    private readonly gameConfigurationService: IGameConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IChatConfigurationService)
    private readonly chatConfigurationService: IChatConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IUserChatStatusService)
    private readonly userChatStatusService: IUserChatStatusService,
    @inject(SERVICE_IDENTIFIERS.IEventBusService)
    private readonly eventBusService: IEventBusService,
    @inject(SERVICE_IDENTIFIERS.ILocalisationService)
    private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.IPersistedStore)
    private readonly tombolaStore: IPersistedStore,
    @inject(SERVICE_IDENTIFIERS.ITogglesService)
    private readonly toggleService: ITogglesService) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public initialise(): Promise<IModalSpawnConfiguration> {
    return new Promise<IModalSpawnConfiguration>((resolve, reject) => {
      const gameConfigurationPromise = this.gameConfigurationService.getGameConfiguration();
      const arcadeGameConfigurationPromise = this.gameConfigurationService.getArcadeGameConfiguration();
      const chatRoomConfigurationPromise = this.chatConfigurationService.getChatConfiguration();
      const cmChatConfigurationPromise = this.getCmChatRoomId();
      const csChatConfigurationPromise = this.getCsChatRoomId();
      Promise.all([gameConfigurationPromise, arcadeGameConfigurationPromise, chatRoomConfigurationPromise, cmChatConfigurationPromise, csChatConfigurationPromise])
             .then((response) => {
                this.gameConfiguration = response[0];
                this.arcadeGameConfiguration = response[1];
                this.chatRoomConfiguration = response[2];
                this.cmChatRoomId = response[3];
                this.csChatRoomId = response[4];
                this.gameConfigurationForWinners = this.buildGameConfigurationForWinners();
                this.isInitialised = true;
                resolve({
                  gameConfiguration: this.gameConfiguration,
                  gameConfigurationForWinners: this.gameConfigurationForWinners,
                  chatRoomConfiguration: this.chatRoomConfiguration,
                  arcadeGameConfiguration: this.arcadeGameConfiguration,
                  cmChatRoomId: this.cmChatRoomId,
                  csChatRoomId: this.csChatRoomId
                });
              });
    });
  }

  public getConfiguration(): IModalSpawnConfiguration | undefined {
    if (!this.isInitialised) {
      return;
    }

    return {
      gameConfiguration: this.gameConfiguration,
      gameConfigurationForWinners: this.gameConfigurationForWinners,
      chatRoomConfiguration: this.chatRoomConfiguration,
      arcadeGameConfiguration: this.arcadeGameConfiguration,
      cmChatRoomId: this.cmChatRoomId,
      csChatRoomId: this.csChatRoomId
    };
}

  private async getCmChatRoomId(): Promise<number> {
    const cmChatConfiguration = await this.chatConfigurationService.getCmChatConfiguration();
    if (cmChatConfiguration.Rooms.length === 0) {
      throw new Error('CM Chat configuration is not available.');
    }
    return cmChatConfiguration.Rooms[0].Id;
  }

  private async getCsChatRoomId(): Promise<number> {
    const csChatConfiguration = await this.chatConfigurationService.getCsChatConfiguration();
    if (csChatConfiguration.Rooms.length === 0) {
      throw new Error('CS Chat configuration is not available.');
    }
    return csChatConfiguration.Rooms[0].Id;
  }

  public spawnCmChatModal(location?: IWindowLocationPreference): void {
    const cmPrefixPromise = this.userChatStatusService.getCMPrefix();
    cmPrefixPromise.then((cmPrefix) => {
      const cmChatModalLaunchOptions: IChatModalLaunchOptions = {
        gameName: 'cmchat',
        frontEndName: 'cmchat',
        id: this.cmChatRoomId,
        isStealthOnEnter: false,
        modalGuid: generateGuid(),
        location,
        chatBackgroundColour: this.tombolaStore.Get(UserPreferences).chatBackgroundColour
      };
      this.spawnModal(WindowType.Chat, `${cmPrefix} Chat`, `${cmPrefix} Chat`, cmChatModalLaunchOptions);
    });
  }

  public spawnCsChatModal(location?: IWindowLocationPreference): void {
      const csChatModalLaunchOptions: IChatModalLaunchOptions = {
        gameName: 'cschat',
        frontEndName: 'cschat',
        id: this.csChatRoomId,
        isStealthOnEnter: false,
        modalGuid: generateGuid(),
        location,
        chatBackgroundColour: this.tombolaStore.Get(UserPreferences).chatBackgroundColour
      };
      this.spawnModal(WindowType.Chat, `CS Chat`, `CS Chat`, csChatModalLaunchOptions);
  }

  public spawnChatModal(gameName: string, chatRoomId: number, chatRoomName?: string, location?: IWindowLocationPreference): void {
    const chatRoomConfig = this.findChatRoomConfigurationForGameNameAndChatRoomId(gameName, chatRoomId);
    const modalOptions: IChatModalLaunchOptions = {
      gameName: chatRoomConfig.GameName,
      frontEndName: chatRoomConfig.FrontEndName,
      id: chatRoomConfig.Id,
      chatRoomName: chatRoomName || chatRoomConfig.Name,
      isStealthOnEnter: true,
      modalGuid: chatRoomConfig.ModalGuid,
      location,
      isArcade: chatRoomConfig.IsArcade || false,
      chatBackgroundColour: this.tombolaStore.Get(UserPreferences).chatBackgroundColour
    };
    const modalTitle = chatRoomName || chatRoomConfig.Name;
    const modalTooltip = `${chatRoomConfig.FrontEndName} Chat - ${modalTitle}`;
    this.spawnModal(WindowType.Chat, modalTitle, modalTooltip, modalOptions);
  }

  public trySpawnChatModalFromRoomName(roomName: string): IGameChatRoomViewModel {
    const chatRoom = this.findChatRoomModelFromRoomName(roomName);
    if (chatRoom) {
      this.spawnChatModal(chatRoom.GameName, chatRoom.Id);
    }
    return chatRoom;
  }

  private findChatRoomModelFromRoomName(roomName: string): IGameChatRoomViewModel & { GameName: string } {
    let chatRoom: IGameChatRoomViewModel = null;
    const roomNameToLowerCase = roomName.replace(/\s/g, '').toLowerCase();
    const game = this.chatRoomConfiguration.find((el) => {
      chatRoom = el.chatRooms.find((el2) => {
        const chatRoomNameToLowerCase = el2.Name.replace(/\s/g, '').toLowerCase();
        return roomNameToLowerCase.startsWith(chatRoomNameToLowerCase);
      });
      return chatRoom !== undefined;
    });

    if (game && chatRoom) {
      return {
        Id: chatRoom.Id,
        Name: chatRoom.Name,
        FrontEndName: chatRoom.FrontEndName,
        ModalGuid: chatRoom.ModalGuid,
        GameName: game.name
      };
    }
  }

  public async spawnGameModal(gameName: string, clubId: number, location?: IWindowLocationPreference): Promise<void> {
    const config = this.findGameConfigurationForGameNameAndClubId(gameName, clubId);
    const modalOptions: IModalLaunchOptions = {
      gameName: config.game.Name,
      frontEndName: config.game.FrontEndName,
      clubName: config.gameClub.Name,
      id: config.gameClub.Id,
      modalGuid: config.gameClub.ModalGuid,
      location
    };
    const gameHasMultipleClubs = config.game.Clubs.length > 1;
    const modalTitle = gameHasMultipleClubs ? `${config.game.FrontEndName} ${config.gameClub.Name}` : config.game.FrontEndName;
    const useIFrameGames = await this.toggleService.isLive('POC_Use_Iframes_Game_Chatmods');
    let isIFrameGame = false;
    if (useIFrameGames) {
      isIFrameGame = this.$lang.game.migratedGames && this.$lang.game.migratedGames.filter((curGameName: string) => {
        return modalOptions.gameName === curGameName;
      }).length > 0;
    }
    this.spawnModal(isIFrameGame ? WindowType.IFrameGame : WindowType.Game, modalTitle, null, modalOptions);
  }

  public spawnArcadeGameModal(gameName: string, location?: IWindowLocationPreference): void {
    const game = this.findArcadeGameConfiguration(gameName);
    const modalOptions: IModalLaunchOptions = {
      gameName: game.Name,
      frontEndName: game.DisplayName,
      modalGuid: game.ModalGuid,
      location
    };
    const modalTitle = game.DisplayName;
    this.spawnModal(WindowType.ArcadeGame, modalTitle, null, modalOptions);
  }

  public spawnWinnersModal(gameName: string, location?: IWindowLocationPreference): void {
    const game = this.findWinnersGameConfigurationForGameName(gameName);
    const modalOptions: IModalLaunchOptions = {
      gameName: game.Name,
      frontEndName: game.FrontEndName,
      modalGuid: game.ModalGuid,
      location
    };
    const modalTitle = `${this.$lang.game.winners.gameWinnersModalTitle} - ${game.FrontEndName}`;
    this.spawnModal(WindowType.GameWinners, modalTitle, null, modalOptions);
  }

  public spawnSettingsModal(location?: IWindowLocationPreference): void {
    const modalOptions: IModalLaunchOptions = {
      gameName: 'User Settings',
      frontEndName: 'User Settings',
      modalGuid: this.settingsGuid,
      location
    };
    const modalTitle = 'User Settings';
    this.spawnModal(WindowType.UserSettings, modalTitle, null, modalOptions);
  }

  public spawnPlayerNotesModal(location?: IWindowLocationPreference): void {
    const modalOptions: IModalLaunchOptions = {
      gameName: 'Player Notes',
      frontEndName: 'Player Notes',
      modalGuid: this.playerNotesGuid,
      location
    };
    const modalTitle = 'Player Notes';
    this.spawnModal(WindowType.PlayerNotes, modalTitle, null, modalOptions);
  }

  public spawnChatContextInteractionModal(chatContextInteractionType: ChatContextInteractionType, selectedPlayerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, selectedChatRoomName?: string, chatContextInteractionSource?: ChatContextInteractionSource, location?: IWindowLocationPreference): void {
    const modalOptions: IChatContextInteractionModalLaunchOptions = {
      gameName: chatContextInteractionType.toString(),
      frontEndName: chatContextInteractionType.toString(),
      modalGuid: generateGuid(),
      location,
      callbackFunction: handleChatContextInteractionCallback,
      chatContextInteraction: chatContextInteractionType,
      playerUsername: selectedPlayerUsername,
      chatRoomName: selectedChatRoomName,
      chatContextInteractionSource
    };
    const modalTitle = 'Chat Option - ' + chatContextInteractionType.toString() + ' - ' + selectedPlayerUsername;
    this.spawnModal(WindowType.ChatContextInteraction, modalTitle, null, modalOptions);
  }

  private findChatRoomConfigurationForGameNameAndChatRoomId(gameName: string, chatRoomId: number): IGameChatRoomViewModel & { GameName: string, FrontEndName: string } {
    const chatRoomConfig = this.chatRoomConfiguration.find((o) => o.name.toLowerCase() === gameName.toLowerCase());
    if (!chatRoomConfig) {
      return null;
    }

    const chatRoom = chatRoomConfig.chatRooms.find((o) => o.Id === chatRoomId);
    if (!chatRoom) {
      return null;
    }

    return Object.assign({}, chatRoom, { GameName: chatRoomConfig.name, FrontEndName: chatRoomConfig.frontEndName });
  }

  private findGameConfigurationForGameNameAndClubId(gameName: string, clubId: number): { game: IGameResponseViewModel, gameClub: IGameClubViewModel } {
    const gameConfig = this.gameConfiguration.find((o) => o.Name.toLowerCase() === gameName.toLowerCase());
    if (!gameConfig) {
      return null;
    }

    const club = gameConfig.Clubs.find((o) => o.Id === clubId);
    if (!club) {
      return null;
    }

    return {
      game: gameConfig,
      gameClub: club
    };
  }

  private findWinnersGameConfigurationForGameName(gameName: string): IGameResponseViewModel {
    return this.gameConfigurationForWinners.find((o) => o.Name.toLowerCase() === gameName.toLowerCase());
  }

  private findArcadeGameConfiguration(gameName: string): IArcadeGameViewModel {
    return this.arcadeGameConfiguration.find((o) => o.Name.toLowerCase() === gameName.toLowerCase());
  }

  private spawnModal(modalType: WindowType, modalTitle?: string, modalTooltip?: string, modalOptions?: IModalLaunchOptions) {
    const eventBus = this.eventBusService.getEventBus();
    eventBus.$emit(MODAL_SPAWN_EVENT_NAME, modalType, modalTitle, modalTooltip, modalOptions);
  }

  private buildGameConfigurationForWinners() {
    const gameConfigurationWinners = this.gameConfiguration.slice(0);
    const additionalOptions = [
      this.buildGameResponseViewModel('Arcade', 'Arcade'),
      this.buildGameResponseViewModel('Quiz Quids', 'Quiz Quids'),
      this.buildGameResponseViewModel('Game Winners', 'Game Winners')
    ];
    return gameConfigurationWinners.concat(...additionalOptions);
  }

  private buildGameResponseViewModel(gameName: string, frontEndName: string): IGameResponseViewModel {
    return {
      Name: gameName,
      FrontEndName: frontEndName,
      Clubs:  [{ Id: -1, Name: 'Empty', ChatRooms: [], ModalGuid: ''}],
      ModalGuid: generateGuid(),
      UrlFriendlyGameName: '',
      IsFreeGame: false
    };
  }
}

export { IModalSpawnAgent, IModalSpawnConfiguration, ModalSpawnAgent, MODAL_SPAWN_EVENT_NAME };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/plugins/modal-manager/modal-spawn-agent.ts